import React, { useState, useEffect } from 'react';
import apiFetch from './api';
import { showModal } from './modalHelper';
import './UserProfile.css';

const ORGAO_OPTIONS = [
  'GNR',
  'PSP',
  'PJ',
  'Bombeiros',
  'B. Sapadores',
  'Militar',
  'Proteção Civil',
  'Policia Municipal',
  'Popular',
  'Guest'
];

export default function UserProfile({ currentUser, onProfileUpdated }) {
  const [profile, setProfile] = useState({
    username: currentUser?.username || '',
    displayName: currentUser?.displayName || currentUser?.name || '',
    role: currentUser?.role || 'user',
    orgao: currentUser?.orgao || 'GNR',
    posto: currentUser?.posto || '',
    unidade: currentUser?.unidade || '',
    nim: currentUser?.nim || ''
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Sincronizar com currentUser quando disponível
  useEffect(() => {
    if (currentUser) {
      setProfile({
        username: currentUser.username || '',
        displayName: currentUser.displayName || currentUser.name || '',
        role: currentUser.role || 'user',
        orgao: currentUser.orgao || 'GNR',
        posto: currentUser.posto || '',
        unidade: currentUser.unidade || '',
        nim: currentUser.nim || ''
      });
    }
  }, [currentUser]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validações locais
    if (!profile.displayName || profile.displayName.trim() === '') {
      setError('O nome de exibição é obrigatório.');
      return;
    }

    if (profile.nim && profile.nim.trim() !== '') {
      const cleanNim = profile.nim.trim();
      if (!/^\d{7}$/.test(cleanNim)) {
        setError('O NIM deve conter exatamente 7 algarismos numéricos.');
        return;
      }
    }

    const isChangingPassword = Boolean(passwordForm.newPassword || passwordForm.currentPassword || passwordForm.confirmPassword);

    if (isChangingPassword) {
      if (!passwordForm.currentPassword) {
        setError('Para alterar a palavra-passe, deve fornecer a sua palavra-passe atual.');
        return;
      }
      if (!passwordForm.newPassword) {
        setError('A nova palavra-passe não pode estar em branco.');
        return;
      }
      if (passwordForm.newPassword.length < 8) {
        setError('A nova palavra-passe deve ter pelo menos 8 caracteres.');
        return;
      }
      if (!/[A-Za-z]/.test(passwordForm.newPassword) || !/[0-9]/.test(passwordForm.newPassword)) {
        setError('A nova palavra-passe deve conter letras e algarismos.');
        return;
      }
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        setError('A confirmação da nova palavra-passe não coincide.');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        displayName: profile.displayName.trim(),
        orgao: profile.orgao,
        posto: profile.posto.trim(),
        unidade: profile.unidade.trim(),
        nim: profile.nim ? profile.nim.trim() : null
      };

      if (isChangingPassword) {
        payload.currentPassword = passwordForm.currentPassword;
        payload.newPassword = passwordForm.newPassword;
      }

      const res = await apiFetch('/api/auth/me/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Falha ao atualizar perfil.');
      }

      setSuccess('Perfil atualizado com sucesso!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });

      if (data.user && onProfileUpdated) {
        onProfileUpdated(data.user, data.token);
      }

      await showModal(
        'Perfil Atualizado',
        'Os seus dados de perfil foram atualizados com sucesso.',
        { confirmText: 'OK' }
      );
    } catch (err) {
      console.error('Erro ao atualizar perfil:', err);
      setError(err.message || 'Erro ao atualizar perfil.');
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'superadmin': return 'Super Administrador';
      case 'admin': return 'Administrador';
      case 'viewer': return 'Visualizador (Apenas Leitura)';
      default: return 'Operador SARIA';
    }
  };

  return (
    <div className="user-profile-container">
      <div className="profile-header-card">
        <div className="profile-avatar-area">
          <div className="profile-avatar-circle">
            <span role="img" aria-label="Utilizador">👤</span>
          </div>
          <div className="profile-titles">
            <h2>{profile.displayName || profile.username}</h2>
            <div className="profile-badge-row">
              <span className={`role-badge role-${profile.role}`}>
                {getRoleLabel(profile.role)}
              </span>
              <span className="orgao-badge">
                🏛️ {profile.orgao || 'GNR'}
              </span>
            </div>
            <p className="profile-username-tag">@{profile.username}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="profile-form-layout" aria-label="Editar Perfil">
        {error && <div className="form-error profile-alert" role="alert">{error}</div>}
        {success && <div className="form-success profile-alert" role="status">{success}</div>}

        <div className="profile-sections-grid">
          {/* SECÇÃO 1: DADOS INSTITUCIONAIS E PESSOAIS */}
          <div className="profile-card-section">
            <div className="section-title-wrap">
              <h3>Informação Pessoal e Operacional</h3>
              <p className="muted">Estes dados identificam as suas ações e preenchem automaticamente os relatórios operacionais.</p>
            </div>

            <div className="form-grid">
              <label className="form-row">
                <span>Nome de Exibição / Nome Completo *</span>
                <input
                  type="text"
                  name="displayName"
                  value={profile.displayName}
                  onChange={handleProfileChange}
                  placeholder="ex: João Manuel Santos"
                  required
                />
              </label>

              <label className="form-row">
                <span>Órgão / Entidade *</span>
                <select
                  name="orgao"
                  value={profile.orgao}
                  onChange={handleProfileChange}
                  required
                >
                  {ORGAO_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>

              <label className="form-row">
                <span>Posto / Graduação</span>
                <input
                  type="text"
                  name="posto"
                  value={profile.posto}
                  onChange={handleProfileChange}
                  placeholder="ex: Guarda, Cabo, Sargento, Tenente, Bombeiro..."
                />
              </label>

              <label className="form-row">
                <span>Unidade / Destacamento / Posto Territorial</span>
                <input
                  type="text"
                  name="unidade"
                  value={profile.unidade}
                  onChange={handleProfileChange}
                  placeholder="ex: Posto Territorial de Mafra, UEPS, etc."
                />
              </label>

              <label className="form-row">
                <span>NIM (Nº de Identificação Militar - 7 dígitos)</span>
                <input
                  type="text"
                  name="nim"
                  maxLength={7}
                  value={profile.nim}
                  onChange={handleProfileChange}
                  placeholder="ex: 2150123"
                />
              </label>

              <label className="form-row">
                <span>Nome de Utilizador (Username)</span>
                <input
                  type="text"
                  value={profile.username}
                  disabled
                  readOnly
                  className="input-disabled"
                  title="O nome de utilizador é fixo no sistema"
                />
              </label>
            </div>
          </div>

          {/* SECÇÃO 2: SEGURANÇA E PALAVRA-PASSE */}
          <div className="profile-card-section">
            <div className="section-title-wrap">
              <h3>Segurança da Conta</h3>
              <p className="muted">Deixe os campos em branco se não pretender alterar a sua palavra-passe.</p>
            </div>

            <div className="form-grid">
              <label className="form-row">
                <span>Palavra-passe Atual</span>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Insira a password atual para confirmar"
                  autoComplete="current-password"
                />
              </label>

              <label className="form-row">
                <span>Nova Palavra-passe</span>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Mínimo 8 caracteres (letras e números)"
                  autoComplete="new-password"
                />
              </label>

              <label className="form-row">
                <span>Confirmar Nova Palavra-passe</span>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Repita a nova palavra-passe"
                  autoComplete="new-password"
                />
              </label>

              <div className="password-requirements-box">
                <span className="req-title">Requisitos da nova palavra-passe:</span>
                <ul>
                  <li>Pelo menos 8 caracteres de comprimento</li>
                  <li>Conter letras (maiúsculas ou minúsculas)</li>
                  <li>Conter algarismos (0-9)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-form-actions">
          <button
            type="submit"
            className="btn-primary btn-save-profile"
            disabled={loading}
          >
            {loading ? 'A guardar alterações...' : '💾 Guardar Alterações'}
          </button>
        </div>
      </form>
    </div>
  );
}
